import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import useTitle from '../../../../hooks/useTitle';

import {
  formErrorMsg,
  shipmentStatuses
} from '../../../../config/index'

import {
  Alert,
  Loading,
  ReactHookFormMuiInput,
  ReactHookFormMuiSelect,
  ReactHookFormMuiTextarea,
  ReactHookFormMuiAutocomplete,
  ReactHookFormMuiDateTimePicker
} from '../../../../common/index'

import {
  Button,
  Stack,
  Container,
} from "@mui/material";

const EditTracking = () => {
  const axios                                            = useAxiosPrivate()
  const { id: trackingID }                               = useParams()
  const { setTitle }                                     = useTitle()
  const [ shipments, setShipments ]                      = useState([])
  const [ loading, setLoading ]                          = useState(true)
  const [ btnLoading, setBtnLoading ]                    = useState(false)
  const [ trackingData, setTrackingData ]                = useState({})
  const { handleSubmit, control, getValues, formState: { errors } } = useForm()
  const [ alert, setAlert ]                              = useState({open: false})
  const { open, message, severity }                      = alert

  useEffect(() => {
    setTitle('Edit Tracking')
  }, [setTitle])

  const formatLabel = shipment => `${shipment.sender_tracking ? `${shipment.sender_tracking} - ` : ''}${shipment.description} (${shipmentStatuses.find((el) => el.value === shipment.status)?.label}) - ${shipment.ID}`

  useEffect( () => {
    const controller = new AbortController();
    const getData = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`/tracking/${trackingID}`, {
          signal: controller.signal
        });
        const data = response.data.shipments.map((shipment) => ({
          value: shipment.ID,
          label: formatLabel( shipment )
        }))
        setShipments( data )
        setTrackingData( response.data )
        setLoading(false)
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') {
          //console.log(err?.response?.data, 'Error');
        }
      }
    }
    getData()
    return () => { 
      controller.abort();
    }
  }, [axios, trackingID])

  let controller;
  const getShipments = async (e, inputValue, reason) => {
    if (reason === 'reset' || !inputValue || inputValue.length < 2) {
      return;
    }
    if (controller) {
      controller.abort()
    }
    controller = new AbortController();
    try {
      const response = await axios.get('/shipments', {
        signal: controller.signal,
        params: {
          search: inputValue,
          count: 0,
          exclude: 'tracking_number',
          per_page: -1
        }
      })
      const data = response.data.map((shipment) => ({
        value: shipment.ID,
        label: formatLabel( shipment )
      }))
      const currentValues = getValues('shipments');
      const filtered      = data.filter(( shipment ) => ! currentValues.some( (currentValue) => currentValue.value === shipment.value ) )
      setShipments( [...filtered, ...currentValues] )
    } catch (err) {
      if (err.code !== 'ERR_CANCELED') {
        //console.log(err?.response?.data, 'Error');
      }
    }
  }

  const onSubmit = async formData => {
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        if ( ! formData[key] ) {
          // Remove empty data
          delete formData[key];
        } else if ( key === 'shipments' ) {
          formData[key] = formData[key].map((shipment) => shipment.value)
        }
      }
    }
    try {
      setBtnLoading(true)
      await axios.post(`/tracking/${trackingID}`, {
        ...formData
      })
      setAlert({
        open: true,
        message: 'Updated successfully.',
        severity: 'success'
      })
    } catch (err) {
      setAlert({
        open: true,
        message: err?.response?.data?.message || 'No server response',
        severity: 'error'
      })
    } finally {
      setBtnLoading(false)
    }
  }

  return (
    <Container disableGutters maxWidth="xs">
      <Alert
        privateLayout
        open={open}
        message={message}
        severity={severity}
        onClose={() => setAlert( {...alert, open: false} )}
      />
      {loading && <Loading privateLayout />}
      {!loading && <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <ReactHookFormMuiInput
            control={control}
            id="tracking_number"
            name="tracking_number"
            label="Tracking number"
            fullWidth
            autoComplete="tracking_number"
            autoFocus
            rules={{required: formErrorMsg.required}}
            error={!!errors.tracking_number}
            helperText={errors.tracking_number && errors.tracking_number?.message}
            defaultValue={trackingData?.tracking_number}
          />

          <ReactHookFormMuiSelect
            control={control}
            id="status"
            name="status"
            label="Shipment status"
            fullWidth
            autoComplete="status"
            rules={{required: formErrorMsg.required}}
            error={!!errors.status}
            helperText={errors.status && errors.status?.message}
            options={[
              {
                  value: 'shipped', 
                  label: 'Shipped'
              }, 
              {
                  value: 'delivered', 
                  label: 'Delivered'
              }
            ]}
            defaultValue={trackingData?.status}
          />

          <ReactHookFormMuiDateTimePicker
            control={control}
            name="date"
            label="Date"
            rules={{required: formErrorMsg.required}}
            error={!!errors.date}
            helperText={errors.date && errors.date?.message}
            defaultValue={trackingData?.date}
          />

          <ReactHookFormMuiAutocomplete
            control={control}
            multiple
            id="shipments"
            name="shipments"
            label="Shipments"
            rules={{required: formErrorMsg.required}}
            error={!!errors.shipments}
            helperText={errors.shipments && errors.shipments?.message}
            options={shipments}
            defaultValue={shipments}
            onInputChange={getShipments}
          />

          <ReactHookFormMuiTextarea
            control={control}
            id="tracking_info"
            name="tracking_info"
            label="Tracking info"
            fullWidth
            autoComplete="tracking_info"
            rules={{required: false}}
            error={!!errors.tracking_info}
            helperText={errors.tracking_info && errors.tracking_info?.message}
            defaultValue={trackingData?.tracking_info}
          />

          <Button
            type="submit"
            fullWidth
          >
            {btnLoading ? 'Please wait...' : 'Update'}
          </Button>
        </Stack>
      </form>}
    </Container>
  )
}

export default EditTracking